/**
 * The to-do deck (core deck.ts): one task per full card, in the same order the Next card ranks them. Swipe right to do it,
 * swipe left for later, tap to open; the buttons and the arrow keys do the same, so a swipe is never the only way.
 * A safety gate (the check-in) is locked: first, alone, and a left swipe springs back. A card that comes due while the
 * deck is open moves to the top with a "Now due" badge. Motion is short (<= 220 ms) and stops under Reduce Motion.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DECK_WORDS, arrangeDeck, buildDeck, dueIndicator, isNowDue, keyIntent, laterOf, newlyDue, swipeIntent,
  type DeckCard, type DeckState, type NextInput,
} from "@logwave/core";
import { prefersReducedMotion } from "../lib/motion.ts";
import { isoToLocalTime } from "../lib/time.ts";
import { useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { AssignLine, useStepIn } from "./Delegate.tsx";
import { GLYPH, nextPath, useDoNext } from "./NextCard.tsx";
import { Button, LinkButton, Pill } from "./ui.tsx";

/** How long a card takes to fly off (ms). Matches the CSS (--dur-base, styles.css :root). */
const FLY_MS = 180;

/** A thin ring that fills toward the due time: blue, amber from 30 minutes before, red once late. */
export function DueRing({ state, progress }: { state: "ok" | "soon" | "late"; progress: number }) {
  const r = 15, c = 2 * Math.PI * r;
  return (
    <svg className={`due-ring due-${state}`} width="40" height="40" viewBox="0 0 40 40" aria-hidden="true" data-progress={progress.toFixed(2)}>
      <circle cx="20" cy="20" r={r} className="due-ring-track" />
      <circle cx="20" cy="20" r={r} className="due-ring-fill" strokeDasharray={c} strokeDashoffset={c * (1 - progress)} transform="rotate(-90 20 20)" />
    </svg>
  );
}

export function TodoDeck({ input, now, reload }: { input: NextInput; /** The clock (ISO). The parent ticks it; tests pin it. */ now: string; reload: () => Promise<void> | void }) {
  const s = useSession();
  const tz = s.location.timezone;
  const timeLabel = (iso: string) => isoToLocalTime(iso, tz);
  const deck = useMemo(() => buildDeck({ ...input, now }), [input, now]);
  const [state, setState] = useState<DeckState>({ later: {}, bumped: {} });
  const [note, setNote] = useState("");
  const [announce, setAnnounce] = useState("");
  const prevNow = useRef<string | undefined>(undefined);

  // D5: whatever came due since the last look moves to the top and says "Now due".
  useEffect(() => {
    const ids = newlyDue(deck.cards, prevNow.current, now);
    prevNow.current = now;
    if (!ids.length) return;
    setState((st) => ({ ...st, bumped: { ...st.bumped, ...Object.fromEntries(ids.map((id) => [id, now])) } }));
    const first = deck.cards.find((c) => c.id === ids[0]);
    if (first) setAnnounce(`${DECK_WORDS.nowDue}: ${first.title}`);
  }, [now]); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = useMemo(() => arrangeDeck(deck.cards, now, state), [deck, now, state]);
  const top = cards[0];

  if (!top) {
    const d = deck.done;
    return (
      <section className="deck" aria-label="Your tasks, as cards">
        <article className="fcard fcard-go deck-card deck-done" data-next-kind="done">
          <div className="fcard-body">
            <h2 className="fcard-title">{d?.title ?? "Everything for today is done"}</h2>
            <p className="next-why">{d?.why}</p>
            {d && <LinkButton big block icon="chevron" iconEnd to={nextPath(d.primary.action)}>{d.primary.label}</LinkButton>}
          </div>
        </article>
      </section>
    );
  }

  const later = (c: DeckCard) => {
    const l = laterOf(c, now);
    if (!l.ok) { setAnnounce(l.reason ?? ""); return false; }
    setState((st) => ({ ...st, later: { ...st.later, [c.id]: l.until! } }));
    const msg = DECK_WORDS.laterNote(timeLabel(l.until!));
    setNote(msg); setAnnounce(msg);
    return true;
  };

  return (
    <section className="deck" aria-label="Your tasks, as cards">
      <p className="deck-count" aria-hidden="true">{DECK_WORDS.count(1, cards.length)}</p>
      <DeckStage key="stage" cards={cards} now={now} state={state} timeLabel={timeLabel} later={later} say={setAnnounce} reload={reload} total={cards.length} />
      {note && <p className="deck-note" aria-hidden="true">{note}</p>}
      <p className="sr-only" role="status" aria-live="polite">{announce}</p>
    </section>
  );
}

function DeckStage({ cards, now, state, timeLabel, later, say, reload, total }: { cards: DeckCard[]; now: string; state: DeckState; timeLabel: (iso: string) => string; later: (c: DeckCard) => boolean; say: (t: string) => void; reload: () => Promise<void> | void; total: number }) {
  const top = cards[0]!;
  const navigate = useNavigate();
  const { tap, busy, markDone } = useDoNext(top.title, top.primary.action, reload);
  // Given to someone else: doing it steps in, and the step-in is recorded as the work starts (core next.ts N6).
  const stepIn = useStepIn();
  const [dx, setDx] = useState(0);
  const [fly, setFly] = useState<"left" | "right" | null>(null);
  const [shake, setShake] = useState(false);
  const drag = useRef<{ x: number; moved: boolean } | null>(null);
  const el = useRef<HTMLElement>(null);
  const reduced = prefersReducedMotion();
  const l = laterOf(top, now);
  const onlyOne = total === 1 && !top.locked;
  const laterOk = l.ok && !onlyOne;
  const ind = dueIndicator(top, now, timeLabel);
  const nowDue = isNowDue(top.id, state, now);

  const doIt = () => { if (top.stepIn) void stepIn(top.stepIn.key); if (tap) void markDone(); else navigate(nextPath(top.primary.action)); };
  const finish = (dir: "left" | "right", act: () => void) => {
    if (reduced) { setDx(0); act(); return; }
    setFly(dir);
    setTimeout(() => { setFly(null); setDx(0); act(); }, FLY_MS);
  };
  const intent = (what: "do" | "later" | "blocked" | "none") => {
    if (what === "do") finish("right", doIt);
    else if (what === "later") finish("left", () => { later(top); });
    else if (what === "blocked") { say(l.ok ? DECK_WORDS.onlyOne : l.reason ?? ""); setShake(true); setTimeout(() => setShake(false), 300); setDx(0); }
    else setDx(0);
  };

  const down = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("a,button,summary")) return;
    drag.current = { x: e.clientX, moved: false };
    try { el.current?.setPointerCapture?.(e.pointerId); } catch { /* old browsers: the drag still works inside the card */ }
  };
  const move = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const x = e.clientX - d.x;
    if (Math.abs(x) > 6) d.moved = true;
    setDx(x);
  };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) { setDx(0); if (!tap) doIt(); return; } // a tap opens it
    const w = el.current?.getBoundingClientRect().width || 360;
    intent(swipeIntent(dx, w, laterOk));
  };
  const key = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest("a,button,summary")) return;
    const k = keyIntent(e.key);
    if (!k) return;
    e.preventDefault();
    intent(k === "do" ? "do" : laterOk ? "later" : "blocked");
  };

  const x = fly === "left" ? "-120%" : fly === "right" ? "120%" : `${dx}px`;
  const lean = fly ? (fly === "left" ? -8 : 8) : Math.max(-8, Math.min(8, dx / 24));
  const kind = top.locked ? "Before food work" : top.critical ? "Food safety" : top.kind === "problem" ? "To fix" : "Task";
  const cue = dx > 24 ? "right" : dx < -24 ? "left" : undefined;

  return (
    <div className="deck-stage">
      {cards.slice(1, 3).map((c, i) => <div key={c.id} className="deck-peek" style={{ ["--peek" as string]: i + 1 }} aria-hidden="true" />)}
      <article
        ref={el} key={top.id} tabIndex={0} role="group" aria-roledescription="task card" aria-label={`${top.title}. ${DECK_WORDS.count(1, total)}. ${DECK_WORDS.hint(laterOk)}`}
        className={`fcard fcard-${top.tone} deck-card${nowDue ? " deck-bump" : ""}${fly ? " deck-fly" : ""}${dx && !fly ? " deck-dragging" : ""}${shake ? " deck-shake" : ""}`}
        data-next-kind={top.kind} data-card-id={top.id} data-cue={cue}
        style={{ transform: `translateX(${x}) rotate(${lean}deg)`, opacity: fly ? 0 : 1 }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { drag.current = null; setDx(0); }} onKeyDown={key}
      >
        <span className="deck-cue deck-cue-do" aria-hidden="true"><Icon name="check" size={20} />{top.primary.label}</span>
        <span className="deck-cue deck-cue-later" aria-hidden="true"><Icon name="clock" size={20} />{top.locked ? "Can't wait" : DECK_WORDS.later}</span>
        <header className="fcard-head">
          <span className="fcard-av deck-av" aria-hidden="true"><Icon name={GLYPH[top.glyph]} size={30} /></span>
          <span className="fcard-kind">{kind}</span>
          {nowDue && <span className="fcard-when"><Pill tone="warn" icon="bell">{DECK_WORDS.nowDue}</Pill></span>}
        </header>
        <div className="fcard-body">
          <h2 className="fcard-title">{top.title}</h2>
          {top.where && <p className="deck-where"><Icon name="pin" size={18} />{top.where}</p>}
          {ind.state !== "none" && (
            <p className={`deck-due due-${ind.state}`} data-due-state={ind.state}>
              <DueRing state={ind.state} progress={ind.progress} />
              <span><Icon name={ind.state === "late" ? "alert" : "clock"} size={16} />{ind.label}</span>
            </p>
          )}
          {top.assign && <AssignLine assign={top.assign} />}
          <p className="next-why">{top.why}</p>
          {tap || top.stepIn
            ? <Button big block icon={tap ? "check" : "chevron"} iconEnd={!tap} busy={busy} onClick={doIt}>{top.primary.label}</Button>
            : <LinkButton big block icon={top.primary.action.kind === "checkin" ? "hands" : "chevron"} iconEnd={top.primary.action.kind !== "checkin"} to={nextPath(top.primary.action)}>{top.primary.label}</LinkButton>}
          <Button variant="secondary" block icon="clock" disabled={!laterOk} reason={!l.ok ? l.reason : onlyOne ? DECK_WORDS.onlyOne : undefined} onClick={() => intent("later")}>{DECK_WORDS.later}</Button>
          <p className="caption deck-hint">{DECK_WORDS.hint(laterOk)}</p>
        </div>
      </article>
    </div>
  );
}
